import React, { useEffect, useState } from "react";
import type { TableProps } from "antd";
import { Popconfirm, Space, Table } from "antd";
import {
  deleteExam,
  filterExam,
  getExams,
} from "../../redux/apiActions/examActions";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { Exam } from "../../redux/apiSlice/examSlice";
import ExamForm from "../../forms/ExamForm";
import { IconPrimary, Popup } from "../../components";
import { DeleteIcon, EditIcon } from "../../assets";
import moment from "moment";
import { usePopup } from "../../hooks/usePopup";
import { getCommonState, getExamState } from "../../redux/store/getState";
import modifyId from "../../utils/modifyId";
import useSearch from "../../hooks/useSearch";
import { clearFilterValues } from "../../redux/apiSlice/commonSlice";
import useUser from "../../hooks/useUser";

type ColumnsType<T extends object = object> = TableProps<T>["columns"];

const ExamTable: React.FC = () => {
  const dispatch = useAppDispatch();
  const { exams, loading } = useAppSelector(getExamState);
  const { filterFrom, filterTo } = useAppSelector(getCommonState);
  const [data, setData] = useState<Exam[]>();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [record, setRecord] = useState<Exam>();
  const popup = usePopup();

  const { examDeletePermission, examEditPermission } = useUser();

  useSearch(exams, setData);

  useEffect(() => {
    if (filterFrom && filterTo) {
      dispatch(filterExam({ from: filterFrom, to: filterTo }));
    } else {
      dispatch(getExams());
    }
  }, [filterFrom, filterTo, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(clearFilterValues());
    };
  }, [dispatch]);

  const handleDelete = (record: Exam) => {
    dispatch(deleteExam(record.id));
  };

  const columns: ColumnsType<Exam> = [
    {
      title: "Date and Time",
      dataIndex: "created_at",
      render: (created_at) =>
        `${moment(created_at).format("DD/MM/YYYY hh:mmA")}`,
    },
    {
      title: "Exam ID",
      dataIndex: "id",
      render: (id) => `${modifyId(id)}`,
    },
    {
      title: "Exam Name",
      dataIndex: "name",
    },
    {
      title: "Product",
      dataIndex: "product",
      render: (product) => <>{product.name}</>,
    },
    {
      title: "Duration",
      dataIndex: "duration",
      render: (duration) => <>{duration} minutes</>,
    },
    {
      title: "Expire By",
      dataIndex: "expire_by",
      render: (expire_by) => `${moment.unix(expire_by).format("DD/MM/YYYY")}`,
    },
    {
      title: "Score",
      dataIndex: "score",
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Space size="middle">
          <IconPrimary
            onClick={() => {
              setRecord(record);
              popup.open();
            }}
            disabled={!examEditPermission}
          >
            <EditIcon />
          </IconPrimary>

          <Popconfirm
            title="Delete the exam"
            description="Are you sure to delete the exam?"
            onConfirm={() => handleDelete(record)}
            okText="Yes"
            cancelText="No"
          >
            <IconPrimary disabled={!examDeletePermission}>
              <DeleteIcon />
            </IconPrimary>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Table
        scroll={{ x: 300 }}
        style={{ marginTop: 20, whiteSpace: "pre", width: "100%" }}
        loading={loading.getExam || loading.deleteExam}
        columns={columns}
        rowKey={(record) => record.id}
        dataSource={data}
        onChange={(p) =>
          setPagination({ current: p.current || 1, pageSize: p.pageSize || 10 })
        }
        pagination={pagination}
        rowClassName={(_, index) => (index % 2 === 0 ? "tr-light" : "tr-dark")}
      />
      <Popup openTrigger={<></>} ref={popup.popupRef}>
        <ExamForm key={record?.id || "exam-form"} record={record} popup={popup} />
      </Popup>
    </>
  );
};

export default ExamTable;
